import type { FC, ReactNode } from 'react'

import { useEffect, useRef, useState } from 'react'
import { cn } from '@/lib/utils'
import { FigLabel } from '@/components/generals/fig-label'
import { Reveal } from '@/components/generals/reveal'

const STATEMENT = 'We measure buildings the way surgeons measure bodies: completely, repeatedly, and before we cut. Then we stay. Every wall we raise keeps reporting back, so the next building is wiser than the last one.'
const WORDS = STATEMENT.split(' ')

/**
 * A pinned statement whose words brighten one by one as the visitor scrolls through it.
 */
export const Manifesto: FC = (): ReactNode => {
    const sectionRef = useRef<HTMLElement>(null)
    const fillRef = useRef<HTMLSpanElement>(null)
    const [lit, setLit] = useState(0)

    useEffect(() => {
        const section = sectionRef.current

        if (!section) return

        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
            setLit(WORDS.length)

            if (fillRef.current) fillRef.current.style.transform = 'scaleY(1)'

            return
        }

        let raf = 0

        const update = (): void => {
            const rect = section.getBoundingClientRect()
            const travel = Math.max(1, rect.height - window.innerHeight)
            const progress = Math.min(1, Math.max(0, (-rect.top + window.innerHeight * 0.15) / travel))
            const count = Math.round(progress * WORDS.length * 1.04)

            if (fillRef.current) fillRef.current.style.transform = `scaleY(${progress.toFixed(4)})`

            setLit(previous => (previous === count ? previous : count))
        }

        const onScroll = (): void => {
            cancelAnimationFrame(raf)
            raf = requestAnimationFrame(update)
        }

        update()
        window.addEventListener('scroll', onScroll, { passive: true })
        window.addEventListener('resize', onScroll)

        return () => {
            cancelAnimationFrame(raf)
            window.removeEventListener('scroll', onScroll)
            window.removeEventListener('resize', onScroll)
        }
    }, [])

    return (
        <section ref={sectionRef} id='manifesto' aria-labelledby='manifesto-title' className='relative h-[230svh] border-t border-hairline'>
            <div className='sticky top-0 flex h-svh flex-col px-gutter pb-16 pt-24 desk:pt-28'>
                <Reveal>
                    <FigLabel fig='06' title='Manifesto' detail='Studio position' note='Read slowly / 34 words' />
                </Reveal>

                <div className='relative mt-10 flex flex-1 items-center desk:mt-0'>
                    <div className='absolute bottom-[12%] left-0 top-[12%] w-px bg-hairline' aria-hidden='true'>
                        <span ref={fillRef} className='absolute inset-0 origin-top bg-signal' style={{ transform: 'scaleY(0)' }} />
                    </div>

                    <h2 id='manifesto-title' className='sr-only'>
                        Manifesto
                    </h2>

                    <p className='pl-6 font-heading text-[clamp(30px,4.6vw,76px)] font-medium leading-[1.06] tracking-[-0.028em] desk:max-w-[24ch] desk:pl-10'>
                        <span className='sr-only'>
                            {STATEMENT}
                        </span>

                        <span aria-hidden='true'>
                            {WORDS.map((word, index) => (
                                <span key={`${word}-${index}`} className={cn('manifesto-word', index < lit && 'is-lit')}>
                                    {word}
                                    {index < WORDS.length - 1 ? ' ' : ''}
                                </span>
                            ))}
                        </span>
                    </p>
                </div>
            </div>
        </section>
    )
}
